/**
 * 项目详情页面
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { projectApi, Project } from '../services/projectApi';
import { toast, confirm } from '../store/toastStore';


const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProject = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await projectApi.getProject(parseInt(id));
      console.log('加载的项目数据:', response.data);
      console.log('当前字数:', response.data.current_word_count);
      console.log('预期字数:', response.data.expected_word_count);
      console.log('完成进度:', response.data.word_count_progress);
      setProject(response.data);
    } catch (error) {
      console.error('加载项目失败:', error);
      toast.error('加载项目失败');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !await confirm({ title: '删除项目', message: '确定要删除这个项目吗？此操作不可恢复。', confirmLabel: '删除', danger: true })) {
      return;
    }

    try {
      await projectApi.deleteProject(parseInt(id));
      navigate('/projects');
    } catch (error) {
      console.error('删除项目失败:', error);
      toast.error('删除项目失败');
    }
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      draft: 'badge badge-default',
      generating: 'badge badge-warning',
      completed: 'badge badge-success',
      archived: 'badge badge-error',
    };
    return <span className={map[status] || map.draft}>{status === 'draft' ? '草稿' : status === 'generating' ? '生成中' : status === 'completed' ? '已完成' : status === 'archived' ? '已归档' : status}</span>;
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-muted-foreground">加载中...</div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12 text-muted-foreground">项目不存在</div>
    );
  }

  return (
    <div>
        {/* 头部 */}
        <div className="mb-8">
          <Link to="/projects" className="text-sm text-muted-foreground hover:text-foreground mb-3 inline-block transition-colors">
            ← 返回项目列表
          </Link>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-foreground truncate max-w-md">{project.project_name}</h1>
              <span className="shrink-0">{getStatusBadge(project.status)}</span>
            </div>
            {/* Tablet 风格操作按钮组 */}
            <div className="flex items-center gap-0 shrink-0 rounded-lg border border-border bg-card overflow-hidden shadow-sm">
              <Link
                to={`/projects/${id}/word`}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 transition-colors border-r border-primary-foreground/20"
                title="Word 编辑"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                Word 编辑
              </Link>
              <Link
                to={`/projects/${id}/wizard`}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition-colors border-r border-border"
                title="标书生成"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" /></svg>
                标书生成
              </Link>
              <Link
                to={`/projects/${id}/edit`}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors border-r border-border"
                title="编辑信息"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" /></svg>
                编辑信息
              </Link>
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors"
                title="删除项目"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                删除
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左列：项目信息 */}
          <div className="lg:col-span-2 space-y-6">
            {project.project_overview && (
              <div className="card">
                <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <span className="w-1 h-4 bg-primary rounded-full" />
                  项目概述
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{project.project_overview}</p>
              </div>
            )}

            {project.document_type === 'proposal' ? (
              project.business_requirements && (
                <div className="card">
                  <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-1 h-4 bg-emerald-500 rounded-full" />
                    业务需求分析
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{project.business_requirements}</p>
                </div>
              )
            ) : (
              project.tech_requirements && (
                <div className="card">
                  <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-1 h-4 bg-amber-500 rounded-full" />
                    技术要求
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{project.tech_requirements}</p>
                </div>
              )
            )}

            {project.other_requirements && (
              <div className="card">
                <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <span className="w-1 h-4 bg-purple-500 rounded-full" />
                  其他要求
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{project.other_requirements}</p>
              </div>
            )}
          </div>

          {/* 右列：统计 + 时间 */}
          <div className="space-y-6">
            {/* 字数统计卡片 */}
            {project.expected_word_count && project.proposal_mode !== 'concise' ? (
              <div className="card">
                <h2 className="text-sm font-semibold text-foreground mb-4">字数统计</h2>
                {project.word_count_progress !== undefined && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                      <span>完成进度</span>
                      <span className="font-semibold text-foreground">{Math.round(project.word_count_progress)}%</span>
                    </div>
                    <div className="w-full bg-accent rounded-full h-2 overflow-hidden">
                      <div className="bg-primary h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(project.word_count_progress, 100)}%` }} />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-accent rounded-lg py-3">
                    <div className="text-2xl font-bold text-foreground">{((project.current_word_count || 0) / 1000).toFixed(1)}k</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">当前字数</div>
                  </div>
                  <div className="bg-accent rounded-lg py-3">
                    <div className="text-2xl font-bold text-foreground">{((project.expected_word_count || 0) / 1000).toFixed(1)}k</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">预期字数</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card">
                <h2 className="text-sm font-semibold text-foreground mb-4">字数统计</h2>
                <div className="text-center py-4">
                  <div className="text-2xl font-bold text-foreground">{((project.current_word_count || 0) / 1000).toFixed(1)}k</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">当前字数</div>
                </div>
              </div>
            )}

            {/* 时间卡片 */}
            <div className="card">
              <h2 className="text-sm font-semibold text-foreground mb-3">时间信息</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">创建</span>
                  <span className="text-foreground">{new Date(project.created_at).toLocaleDateString('zh-CN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">更新</span>
                  <span className="text-foreground">{new Date(project.updated_at).toLocaleDateString('zh-CN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">类型</span>
                  <span className="text-foreground">{project.document_type === 'bid' ? '标书' : project.document_type === 'proposal' ? '方案' : project.document_type || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default ProjectDetail;
